import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as adminApi from '../api/adminApi';

const ACCESS_TOKEN_KEY = 'adminAccessToken';
const AuthContext = createContext(null);

/**
 * Extract bearer token from login/refresh payloads (legacy; REST uses httpOnly cookies).
 * @param {Record<string, unknown> | null | undefined} response
 * @returns {string | null}
 */
function parseAccessToken(response) {
  if (!response || typeof response !== 'object') return null;
  return (
    response.accessToken ||
    response.token ||
    response.access_token ||
    response.authToken ||
    response.auth_token ||
    null
  );
}

/**
 * Admin session state: profile, cookie-backed auth, login/logout/refresh.
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [accessToken, setAccessToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  });
  const [loading, setLoading] = useState(true);

  const saveToken = useCallback((token) => {
    if (typeof window === 'undefined') return;
    if (token) {
      window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
      setAccessToken(token);
    } else {
      window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      setAccessToken(null);
    }
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.fetchMe();
      setAdmin(data?.admin || data);
      // REST auth uses httpOnly cookies; clear any stale token left in localStorage.
      saveToken(null);
    } catch {
      setAdmin(null);
      saveToken(null);
    } finally {
      setLoading(false);
    }
  }, [saveToken]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = useCallback(async (email, password) => {
    const data = await adminApi.loginAdmin({ email, password });
    const token = parseAccessToken(data);
    saveToken(token);
    const next = data?.admin || data;
    setAdmin(next);
    return next;
  }, [saveToken]);

  const logout = useCallback(async () => {
    try {
      await adminApi.logoutAdmin();
    } finally {
      setAdmin(null);
      saveToken(null);
    }
  }, [saveToken]);

  const refreshSession = useCallback(async () => {
    setLoading(true);
    try {
      const refreshData = await adminApi.refreshAdminToken();
      const token = parseAccessToken(refreshData);
      if (token) {
        saveToken(token);
      }
      const data = await adminApi.fetchMe();
      setAdmin(data?.admin || data);
      return data;
    } catch (error) {
      setAdmin(null);
      saveToken(null);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [saveToken]);

  const value = useMemo(
    () => ({
      admin,
      accessToken,
      loading,
      isAuthenticated: Boolean(admin),
      login,
      logout,
      refreshSession,
    }),
    [admin, accessToken, loading, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * @returns {{ admin: unknown, accessToken: string | null, loading: boolean, isAuthenticated: boolean, login: (email: string, password: string) => Promise<unknown>, logout: () => Promise<void>, refreshSession: () => Promise<unknown> }}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
